import {useCallback} from 'react';

import {useMcpTemporaryLimits} from 'src/components/connector-limits-form';
import {
    getUserTempLimits,
    removeUserTemporaryLimit,
    saveUserTemporaryLimit,
} from 'src/openapi/admin/apis/UserTemporaryLimitsApi';
import {useAppDispatch} from 'src/store/hooks';

import type {SaveOptions} from 'src/components/save-with-reason/save-with-reason-button';
import type {TempLimit} from 'src/openapi/admin/models/TempLimit';

type Props = {
    userId: number,
};

export const useUserMcpTemporaryLimits = ({userId}: Props) => {
    const apiDispatch = useAppDispatch();

    const getTempLimits = useCallback(
        (signal: AbortSignal) => apiDispatch(
            getUserTempLimits({userId}, {signal}),
        ),
        [userId, apiDispatch],
    );

    const saveTempLimit = useCallback(
        (
            limits: TempLimit[],
            expiresOn: string,
            signal: AbortSignal,
            options?: SaveOptions,
        ) => apiDispatch(
            saveUserTemporaryLimit(
                {
                    userId,
                    expiresOn,
                    userLimitsSaveRequest: {limits, reason: options?.reason},
                },
                {signal},
            ),
        ),
        [userId, apiDispatch],
    );

    const removeTempLimit = useCallback(
        (
            limitIds: string[],
            signal: AbortSignal,
            options?: SaveOptions,
        ) => apiDispatch(
            removeUserTemporaryLimit(
                {
                    userId,
                    userLimitsRemoveRequest: {limitIds, reason: options?.reason},
                },
                {signal},
            ),
        ),
        [userId, apiDispatch],
    );

    return useMcpTemporaryLimits({
        getTempLimits,
        saveTempLimit,
        removeTempLimit,
    });
};
